import { getBody, getById } from '../utils/dom.js';

/**
 * Handle the global site header behavior.
 *
 * @param {Document} root
 */
export function initHeader(root = document) {
  const header = getById('site-header', root);
  const toggle = getById('mobile-menu-toggle', root);
  const nav = getById('primary-navigation', root);
  const body = getBody(root);

  if (!header) {
    return;
  }

  const appointmentWidget = header.querySelector('[data-header-whatsapp]');
  const appointmentToggle = header.querySelector('[data-header-whatsapp-toggle]');
  const appointmentPanel = header.querySelector('[data-header-whatsapp-panel]');

  const setAppointmentOpen = (open) => {
    if (!appointmentWidget || !appointmentToggle || !appointmentPanel) {
      return;
    }

    if (open) {
      appointmentPanel.hidden = false;
    }

    appointmentWidget.classList.toggle('is-open', open);
    appointmentToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    appointmentPanel.setAttribute('aria-hidden', open ? 'false' : 'true');

    if (!open) {
      window.setTimeout(() => {
        if (!appointmentWidget.classList.contains('is-open')) {
          appointmentPanel.hidden = true;
        }
      }, 180);
    }
  };

  if (appointmentWidget && appointmentToggle && appointmentPanel) {
    appointmentToggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setAppointmentOpen(!appointmentWidget.classList.contains('is-open'));
    });

    appointmentPanel.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  const treatmentWidgets = Array.from(root.querySelectorAll('[data-treatment-whatsapp]'));

  const setTreatmentOpen = (widget, open) => {
    const toggleButton = widget?.querySelector('[data-treatment-whatsapp-toggle]');
    const panel = widget?.querySelector('[data-treatment-whatsapp-panel]');

    if (!widget || !toggleButton || !panel) {
      return;
    }

    if (open) {
      panel.hidden = false;
    }

    widget.classList.toggle('is-open', open);
    toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');

    if (!open) {
      window.setTimeout(() => {
        if (!widget.classList.contains('is-open')) {
          panel.hidden = true;
        }
      }, 180);
    }
  };

  const closeTreatmentWidgets = (exceptWidget = null) => {
    treatmentWidgets.forEach((widget) => {
      if (widget !== exceptWidget) {
        setTreatmentOpen(widget, false);
      }
    });
  };

  treatmentWidgets.forEach((widget) => {
    const toggleButton = widget.querySelector('[data-treatment-whatsapp-toggle]');
    const panel = widget.querySelector('[data-treatment-whatsapp-panel]');

    toggleButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const shouldOpen = !widget.classList.contains('is-open');
      closeTreatmentWidgets(widget);
      setTreatmentOpen(widget, shouldOpen);
    });

    panel?.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  });

  const syncScrollState = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  };

  const syncMenuState = (isOpen) => {
    if (!toggle || !nav || !body) {
      return;
    }

    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      isOpen ? 'Fechar menu principal' : 'Abrir menu principal',
    );
    nav.classList.toggle('is-open', isOpen);
    header.classList.toggle('is-menu-open', isOpen);
    body.classList.toggle('menu-open', isOpen);
  };

  const closeMenu = () => {
    nav?.querySelectorAll('[data-mobile-submenu-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.closest('.site-header__nav-item')?.classList.remove('is-submenu-open');
    });

    syncMenuState(false);
  };

  const openMenu = () => {
    syncMenuState(true);
  };

  window.addEventListener('scroll', syncScrollState, { passive: true });
  syncScrollState();

  if (!toggle || !nav || !body) {
    return;
  }

  syncMenuState(false);

  nav.querySelectorAll('[data-mobile-submenu-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      if (window.innerWidth > 900) {
        return;
      }

      event.preventDefault();

      const item = trigger.closest('.site-header__nav-item');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      nav.querySelectorAll('[data-mobile-submenu-trigger]').forEach((otherTrigger) => {
        if (otherTrigger === trigger) {
          return;
        }

        otherTrigger.setAttribute('aria-expanded', 'false');
        otherTrigger.closest('.site-header__nav-item')?.classList.remove('is-submenu-open');
      });

      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      item?.classList.toggle('is-submenu-open', !isOpen);
    });
  });

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      closeMenu();
      return;
    }

    openMenu();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setAppointmentOpen(false);
      closeTreatmentWidgets();
      closeMenu();
    }
  });

  document.addEventListener('click', (event) => {
    if (appointmentWidget && !appointmentWidget.contains(event.target)) {
      setAppointmentOpen(false);
    }

    treatmentWidgets.forEach((widget) => {
      if (!widget.contains(event.target)) {
        setTreatmentOpen(widget, false);
      }
    });

    if (
      nav.classList.contains('is-open') &&
      !nav.contains(event.target) &&
      !toggle.contains(event.target)
    ) {
      closeMenu();
    }
  });

  nav.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      if (window.innerWidth <= 900 && link.matches('[data-mobile-submenu-trigger]')) {
        return;
      }

      if (window.innerWidth <= 900) {
        window.setTimeout(closeMenu, 120);
      }
    });
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 900) {
      closeMenu();
    }
  });
}
